import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useLocation } from "react-router-dom"
import { Shield, Plus, Save, Smartphone, RefreshCw, LogOut, ArrowUpDown, ArrowDown, ArrowUp, ChevronRight, Search, Users } from "lucide-react"
import ClientService from "../../services/client/ClientService";
import { displayBusinessUrl } from "../../utils/businessUrl";
import AnalyticsService from "../../services/analytics/AnalyticsService";
import BusinessHeader from "./components/BusinessHeader";
import AttentionFeed from "./components/AttentionFeed";
import { setAppBadge } from "../../utils/appBadge";
import QuickPaySheet from "./components/QuickPaySheet";
import PushToggle from "./components/PushToggle";
import usePullToRefresh from "../../hooks/usePullToRefresh";
import { getSubscriptionStatus as subscriptionStatus } from "../../utils/subscriptionStatus";
import PasswordInput from "../../components/PasswordInput";
import ClientDetailPanel from "./components/ClientDetailPanel";
import Sheet from "./components/Sheet";
import PlatformSettings from "./components/PlatformSettings";
import ServerHealth from "./components/ServerHealth";
import { PERIODS, resolvePeriod, fmtInt, fmtMoney, relativeDay, totalCreated } from "./usagePeriods";
import { PRICING, computePrice, nextCycleAddons } from "./pricing";
import { showError, showSuccess, showWarning } from "../../components/alertas";
import { mostrarSpinner, ocultarSpinner } from "../../components/Spinner";
import { BRANDING_THEME as BRAND_COLORS } from "../../constants/brandingTheme";
import "./superadmin.css";
import logger from "../../utils/logger";

/** Booleanos planos de todos los add-ons del catálogo (lo que llega de GraphQL puede ser null o traer __typename). */
const pickAddons = (src) => Object.fromEntries(Object.keys(PRICING.addons).map((k) => [k, !!src?.[k]]));

/** true en pantallas >= 992px (bootstrap lg). El panel cambia de cards a tabla. */
function useIsDesktop() {
  const query = "(min-width: 992px)";
  const [matches, setMatches] = useState(() => (typeof window !== "undefined" ? window.matchMedia(query).matches : false));
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = (e) => setMatches(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return matches;
}

function SuperadminPanel({ onLogout }) {
  const isDesktop = useIsDesktop();
  const [activeTab, setActiveTab] = useState("clients") // "clients" o "platform"
  const [showNewClientForm, setShowNewClientForm] = useState(false)
  const [expandedClient, setExpandedClient] = useState(null)

  const [clients, setClients] = useState([])
  const [editingData, setEditingData] = useState({})

  // Resumen legible del estado de la vigencia: badge + días restantes.
  const STATUS_COLORS = { active: BRAND_COLORS.success, grace: BRAND_COLORS.warning, expired: BRAND_COLORS.error, inactive: BRAND_COLORS.text.muted, unknown: BRAND_COLORS.text.muted };
  const getSubscriptionStatus = (expiresAtStr, isActive) => {
    const st = subscriptionStatus(expiresAtStr, isActive);
    return { ...st, color: STATUS_COLORS[st.key] };
  };

  // ---- Uso (client_metrics), estado en vivo de WhatsApp y leads ----
  const [periodKey, setPeriodKey] = useState("this_month");
  const period = useMemo(() => resolvePeriod(periodKey), [periodKey]);
  const [overview, setOverview] = useState(null);
  const [prevOverview, setPrevOverview] = useState(null);
  const [waLive, setWaLive] = useState({});
  const [leads, setLeads] = useState([]);
  const [serverHealth, setServerHealth] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [sortKey, setSortKey] = useState("activity");
  const [sortDir, setSortDir] = useState("desc");
  const toggleSort = (key) => {
    if (key === sortKey) setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    else { setSortKey(key); setSortDir(key === "name" || key === "expires" ? "asc" : "desc"); }
  };

  const loadUsage = useCallback(async () => {
    setUsageLoading(true);
    try {
      const [cur, prev, wa, pending, health] = await Promise.all([
        AnalyticsService.getOverview(period.from, period.to),
        AnalyticsService.getOverview(period.prevFrom, period.prevTo),
        AnalyticsService.getWhatsappLiveStatuses().catch(() => ({})),
        AnalyticsService.getPendingLeads(30).catch(() => []),
        AnalyticsService.getServerHealth().catch(() => null),
      ]);
      setOverview(cur);
      setPrevOverview(prev);
      setWaLive(wa || {});
      setLeads(pending);
      setServerHealth(health);
    } catch (error) {
      logger.error("Error cargando uso:", error);
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        onLogout?.();
      }
    } finally {
      setUsageLoading(false);
    }
  }, [period, onLogout]);

  useEffect(() => { loadUsage(); }, [loadUsage]);

  const usageByClient = useMemo(() => {
    const map = {};
    for (const row of overview?.clients || []) map[row.clientId] = row;
    return map;
  }, [overview]);
  const prevUsageByClient = useMemo(() => {
    const map = {};
    for (const row of prevOverview?.clients || []) map[row.clientId] = row;
    return map;
  }, [prevOverview]);

  // Conexiones: WhatsApp del Map vivo de Baileys (fallback al status guardado si el
  // back aún no reporta), Calendar = staff con Google listo vs asientos pagados.
  const connectionInfo = (client) => {
    const live = waLive[client.id];
    const waConnected = live ? live.connected : client.whatsintegration?.status === "ready";
    const calendarsReady = (client.staff || []).filter((s) => s.status === "ready").length;
    const seats = client.subscription?.seats ?? 1;
    return { waConnected, calendarsReady, seats };
  };

  const portfolio = useMemo(() => {
    const acc = { total: clients.length, active: 0, grace: 0, expired: 0, mrr: 0, waConnected: 0, waDown: 0 };
    for (const c of clients) {
      const st = getSubscriptionStatus(c.subscription?.expiresAt, c.subscription?.isActive);
      const { waConnected } = connectionInfo(c);
      if (waConnected) acc.waConnected++;
      if (st.label === "Activa" || st.label === "En gracia") {
        acc.mrr += computePrice(c.subscription?.seats, c.subscription?.addons);
        if (!waConnected) acc.waDown++;
      }
      if (st.label === "Activa") acc.active++;
      else if (st.label === "En gracia") acc.grace++;
      else if (st.label === "Vencida") acc.expired++;
    }
    return acc;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients, waLive]);

  const sortedClients = useMemo(() => {
    const u = (c) => usageByClient[c.id] || {};
    const sorters = {
      activity: (a, b) => ((u(b).messagesReceived || 0) + totalCreated(u(b))) - ((u(a).messagesReceived || 0) + totalCreated(u(a))),
      messages: (a, b) => (u(b).messagesReceived || 0) - (u(a).messagesReceived || 0),
      appointments: (a, b) => totalCreated(u(b)) - totalCreated(u(a)),
      revenue: (a, b) => ((u(b).revenueBooked || 0) - (u(b).revenueCancelled || 0)) - ((u(a).revenueBooked || 0) - (u(a).revenueCancelled || 0)),
      expires: (a, b) => new Date(a.subscription?.expiresAt || 0) - new Date(b.subscription?.expiresAt || 0),
      name: (a, b) => (a.business?.name || "").localeCompare(b.business?.name || ""),
    };
    const sorted = [...clients].sort(sorters[sortKey] || sorters.activity);
    // Los sorters ordenan "desc" (más primero) salvo name/expires que son "asc" natural.
    const naturalAsc = sortKey === "name" || sortKey === "expires";
    return (sortDir === "asc") === naturalAsc ? sorted : sorted.reverse();
  }, [clients, usageByClient, sortKey, sortDir]);

  const [query, setQuery] = useState("");
  const visibleClients = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sortedClients;
    return sortedClients.filter((c) =>
      (c.business?.name || "").toLowerCase().includes(q) ||
      (c.slug || "").toLowerCase().includes(q) ||
      (c.auth?.username || "").toLowerCase().includes(q));
  }, [sortedClients, query]);

  const selectedClient = useMemo(() => clients.find((c) => c.id === expandedClient) || null, [clients, expandedClient]);

  const [detailTab, setDetailTab] = useState("uso");
  const [quickPayClient, setQuickPayClient] = useState(null);

  /** Mantiene ?client=&tab= en la URL para poder compartir/volver al mismo cliente (sin re-render). */
  const syncUrl = (params) => {
    const url = new URL(window.location.href);
    ["client", "tab", "focus"].forEach((k) => url.searchParams.delete(k));
    Object.entries(params).forEach(([k, v]) => v && url.searchParams.set(k, v));
    window.history.replaceState(null, "", url.pathname + (url.search || ""));
  };

  const openClient = (client, tab = "uso") => {
    if (!client) return;
    setDetailTab(tab);
    setExpandedClient(client.id);
    setEditingData(getEditingDataFromClient(client));
    setPaymentForm({ reference: "", periodMonths: 1, expiresAt: "" });
    loadPayments(client.id);
    syncUrl({ client: client.id, tab: tab !== "uso" ? tab : "" });
  };

  const closeClient = () => {
    setExpandedClient(null);
    setPayments([]);
    syncUrl({});
  };

  const toggleExpanded = (client) => (expandedClient === client.id ? closeClient() : openClient(client));

  // Deep links: /admin?client=<id>&tab=uso|config|pagos, ?tab=platform, ?focus=leads.
  // Vienen de las notificaciones push y de links que te mandas a ti mismo.
  const location = useLocation();
  const deepLinkApplied = useRef(false);
  useEffect(() => {
    if (deepLinkApplied.current || clients.length === 0) return;
    const q = new URLSearchParams(location.search);
    if (q.get("tab") === "platform") { setActiveTab("platform"); deepLinkApplied.current = true; return; }
    const id = q.get("client");
    if (id) {
      const client = clients.find((c) => c.id === id);
      if (client) openClient(client, ["uso", "config", "pagos"].includes(q.get("tab")) ? q.get("tab") : "uso");
      deepLinkApplied.current = true;
    } else if (q.get("focus") === "leads") {
      document.querySelector(".sa-attention")?.scrollIntoView({ behavior: "smooth", block: "start" });
      deepLinkApplied.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients, location.search]);

  const waHrefFor = (client) => {
    const n = client?.whatsintegration?.connectedNumber;
    return n ? `https://wa.me/${String(n).replace(/\D/g, "")}` : null;
  };

  /** Datos derivados de una fila (compartidos por la vista de cards y la de tabla). */
  const rowData = (client) => {
    const usage = usageByClient[client.id];
    const st = getSubscriptionStatus(client.subscription?.expiresAt, client.subscription?.isActive);
    const conn = connectionInfo(client);
    const created = totalCreated(usage);
    const idle = !usage || ((usage.messagesReceived || 0) + created === 0);
    const lastEver = usage?.lastActiveEver || overview?.lastActivity?.[client.id] || null;
    const unhealthy = (usage?.remindersFailed || 0) + (usage?.messagesErrors || 0) + (usage?.whatsappSessionDrops || 0);
    const revenue = (usage?.revenueBooked || 0) - (usage?.revenueCancelled || 0);
    const confirmPct = created > 0 ? Math.round(((usage?.appointmentsConfirmed || 0) / created) * 100) : null;
    return { usage, st, conn, created, idle, lastEver, unhealthy, revenue, confirmPct };
  };

  /** Items de la bandeja "Requiere atención", ordenados por severidad. */
  const attentionItems = useMemo(() => {
    const items = [];
    const sev = { 0: "critical", 1: "high", 2: "normal" };
    for (const c of clients) {
      const { st, conn, usage, idle, lastEver } = rowData(c);
      const paying = st.label === "Activa" || st.label === "En gracia";
      const name = c.business?.name || c.slug;
      const wa = waHrefFor(c);
      if (paying && !conn.waConnected) {
        items.push({ id: `wa:${c.id}`, kind: "wa_down", severity: sev[0], client: c, tab: "uso", waHref: wa,
          title: `${name} · WhatsApp caído`, detail: "Está pagando y su bot no responde. Debe volver a vincular." });
      }
      if (st.label === "En gracia") {
        items.push({ id: `grace:${c.id}`, kind: "grace", severity: sev[0], client: c, tab: "pagos", pay: true, waHref: wa,
          title: `${name} · en periodo de gracia`, detail: `${st.relative}. ${fmtMoney(computePrice(c.subscription?.seats, nextCycleAddons(c.subscription)))}/mes.` });
      } else if (st.label === "Activa" && st.daysLeft !== undefined && st.daysLeft <= 3) {
        items.push({ id: `exp:${c.id}`, kind: "expiring", severity: sev[1], client: c, tab: "pagos", pay: true, waHref: wa,
          title: `${name} · ${st.daysLeft === 0 ? "vence hoy" : `vence en ${st.daysLeft} día${st.daysLeft === 1 ? "" : "s"}`}`, detail: `${fmtMoney(computePrice(c.subscription?.seats, nextCycleAddons(c.subscription)))}/mes. Registra el pago cuando llegue la transferencia.` });
      }
      if ((usage?.remindersFailed || 0) > 0) {
        items.push({ id: `rem:${c.id}`, kind: "reminders_failed", severity: sev[1], client: c, tab: "uso", waHref: wa,
          title: `${name} · ${fmtInt(usage.remindersFailed)} recordatorio${usage.remindersFailed === 1 ? "" : "s"} sin enviar`, detail: "Sus clientes no recibieron el aviso. Suele ser el WhatsApp del negocio." });
      }
      if ((usage?.messagesErrors || 0) > 0) {
        items.push({ id: `err:${c.id}`, kind: "bot_errors", severity: sev[2], client: c, tab: "uso",
          title: `${name} · ${fmtInt(usage.messagesErrors)} error${usage.messagesErrors === 1 ? "" : "es"} del bot`, detail: "Excepciones al procesar mensajes en el periodo." });
      }
      if (st.label === "Activa" && idle && lastEver && relativeDay(lastEver).startsWith("hace") && (Date.now() - new Date(lastEver)) > 14 * 864e5) {
        items.push({ id: `idle:${c.id}`, kind: "idle", severity: sev[2], client: c, tab: "uso", waHref: wa,
          title: `${name} · sin actividad`, detail: `Última actividad ${relativeDay(lastEver)}. Paga pero no usa el producto: riesgo de baja.` });
      }
    }
    const weekAgo = Date.now() - 7 * 864e5;
    for (const l of leads) {
      if (new Date(l.updatedAt || l.createdAt || 0) < weekAgo) continue;
      items.push({ id: `lead:${l.phone}`, kind: "lead", severity: l.status === "verified" ? sev[1] : sev[2], waHref: `https://wa.me/${l.phone}`,
        title: `${l.phone} · ${l.status === "verified" ? "verificó su número y no terminó" : "pidió su código y no siguió"}`, detail: "Registro a medias. Un WhatsApp tuyo suele destrabarlo." });
    }
    const order = { critical: 0, high: 1, normal: 2 };
    return items.sort((a, b) => order[a.severity] - order[b.severity]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients, usageByClient, waLive, leads, overview]);

  // Badge del ícono de la PWA = pendientes que exigen acción (crítico + alto); los
  // informativos (sin actividad, leads) no cuentan. Se recalcula con cada carga.
  useEffect(() => {
    setAppBadge(attentionItems.filter((it) => it.severity === "critical" || it.severity === "high").length);
  }, [attentionItems]);

  const [paymentForm, setPaymentForm] = useState({ reference: "", periodMonths: 1, expiresAt: "" });
  const [payments, setPayments] = useState([]);

  const loadPayments = async (clientId) => {
    try {
      const data = await ClientService.getPayments(clientId);
      setPayments(Array.isArray(data) ? data : []);
    } catch (error) {
      logger.error("Error cargando pagos:", error);
      setPayments([]);
    }
  };

  const fetchClients = useCallback(async () => {
    try {
      const data = await ClientService.getAllClients();
      setClients(data);
    } catch (error) {
      logger.error("Error fetching clients:", error);
    }
  }, []);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  // Recargar clientes, uso y estado de WhatsApp: botón de la barra y jalar hacia abajo (PWA).
  const refreshAll = useCallback(() => Promise.all([fetchClients(), loadUsage()]), [fetchClients, loadUsage]);
  const ptr = usePullToRefresh(refreshAll);

  const [newClient, setNewClient] = useState({
    name: "",
    username: "",
    prefixUrl: "",
    password: ""
  })

  const handleAddClient = async () => {
    if (newClient.name && newClient.username && newClient.prefixUrl && newClient.password) {
      try {
        await ClientService.createClient(newClient);

        // Recargar desde GraphQL para tener la forma consistente (con el campo `id`).
        // El push optimista con la respuesta REST traía `_id` pero no `id`, y al
        // editar ese cliente recién creado se mandaba `clients/undefined`.
        await fetchClients();

        setNewClient({ name: "", username: "", prefixUrl: "", password: "" });
        setShowNewClientForm(false);
      } catch (error) {
        const backendMsg = error.response?.data?.message || error.message;
        if (backendMsg.includes('E11000 duplicate key error')) {
          showError('Error al crear cliente', 'El slug proporcionado ya existe. Por favor, elige otro slug único.');
        } else {
          showError('Error al crear cliente', backendMsg);
        }
      }
    } else {
      showWarning("Faltan datos", "Por favor, completa todos los campos del formulario.");
    }
  }

  /** `applyAddonsNow`: guarda y además vuelve vigentes los add-ons programados sin esperar al corte (pruebas). */
  const handleUpdateClient = async (clientId, { applyAddonsNow = false } = {}) => {
    if (!clientId || clientId === "undefined") {
      showError("No se pudo actualizar", "Recargando la lista de clientes, intenta de nuevo.");
      await fetchClients();
      return;
    }
    try {
      mostrarSpinner();
      const updateData = buildUpdateData(editingData, { applyAddonsNow });
      await ClientService.updateClient(clientId, updateData);
      showSuccess("Cliente actualizado", applyAddonsNow ? "Add-ons aplicados desde hoy." : "Los cambios se han guardado correctamente.");
      fetchClients();
      setExpandedClient(null);
    } catch (error) {
      logger.error("Error actualizando cliente:", error);
      showError("Error actualizando cliente", "Por favor, intenta de nuevo.");
    } finally {
      ocultarSpinner(false);
    }
  }

  /**
   * Registra un pago SPEI y extiende la suscripción. Lo usan el formulario de Pagos
   * (con lo capturado ahí) y el pago rápido de la bandeja (valores prellenados).
   */
  const registerPayment = async (client, { months = 1, reference = "", expiresAt = "" } = {}) => {
    const clientId = client?.id;
    if (!clientId) return false;
    try {
      mostrarSpinner();
      const periodMonths = Number(months) || 1;
      const seats = client.subscription?.seats ?? editingData.seats;
      // Se cobra lo que queda vigente con este pago: los add-ons programados, no los de hoy.
      const addons = client.subscription ? nextCycleAddons(client.subscription) : editingData.addonsNextCycle;
      const amount = computePrice(seats, addons) * periodMonths;
      const overrideExpiresAt = expiresAt || undefined;
      const result = await ClientService.registerPayment({
        clientId,
        periodMonths,
        amount,
        reference: reference?.trim() || undefined,
        expiresAt: overrideExpiresAt,
      });
      showSuccess(
        "Pago registrado",
        overrideExpiresAt
          ? `Vigencia fijada al ${overrideExpiresAt} por $${amount} MXN.`
          : `Suscripción extendida ${periodMonths} mes(es) por $${amount} MXN.`
      );
      // Refleja la nueva expiración en el formulario si ese cliente está abierto
      const newExp = result?.newExpiresAt ? new Date(result.newExpiresAt).toISOString().slice(0, 10) : undefined;
      if (expandedClient === clientId && newExp) {
        setEditingData((ed) => ({ ...ed, status: true, subscriptionExpiresAt: newExp, nextPaymentDate: newExp }));
        await loadPayments(clientId);
      }
      setPaymentForm({ reference: "", periodMonths: 1, expiresAt: "" });
      await fetchClients();
      return true;
    } catch (error) {
      logger.error("Error registrando pago:", error);
      const backendMsg = error.response?.data?.message || error.message;
      showError("Error al registrar pago", backendMsg);
      return false;
    } finally {
      ocultarSpinner(false);
    }
  };

  const handleRegisterPayment = (clientId) =>
    registerPayment(clients.find((c) => c.id === clientId), {
      months: paymentForm.periodMonths,
      reference: paymentForm.reference,
      expiresAt: paymentForm.expiresAt,
    });

  const getEditingDataFromClient = (client) => {
    const formatDateForInput = (d) => {
      if (!d) return "";
      const date = new Date(d);
      if (isNaN(date.getTime())) return "";
      return date.toISOString().slice(0, 10); // yyyy-MM-dd
    };

    return {
      businessName: client.business?.name || "",
      username: client.auth?.username || "",
      prefix: client.slug || "",
      status: client.subscription?.isActive || false,
      planName: client.subscription?.planName || "Base",
      seats: client.subscription?.seats ?? 1,
      aiMonthlyCap: client.subscription?.aiMonthlyCap ?? 1500,
      // Solo lectura: lo vigente hoy. Lo editable es addonsNextCycle (entra con el próximo pago).
      addons: pickAddons(client.subscription?.addons),
      addonsNextCycle: pickAddons(nextCycleAddons(client.subscription)),
      // Vacío = lo programado entra con el corte del ciclo.
      addonsNextCycleAt: formatDateForInput(client.subscription?.addonsNextCycleAt),
      subscriptionStartDate: formatDateForInput(client.subscription?.startsAt),
      subscriptionExpiresAt: formatDateForInput(client.subscription?.expiresAt),
      nextPaymentDate: formatDateForInput(client.subscription?.nextPaymentDate),
      password: "",
    }
  }


  const buildUpdateData = (editingData, { applyAddonsNow = false } = {}) => {
    const updatedData = {
      auth: {
        username: editingData.username,
        ...(editingData.password && { password: editingData.password })
      },
      slug: editingData.prefix,
      subscription: {
        isActive: !!editingData.status,
        planName: editingData.planName || "Base",
        seats: Number(editingData.seats) || 1,
        aiMonthlyCap: Math.max(0, parseInt(editingData.aiMonthlyCap, 10) || 0),
        // `addons` (lo vigente) solo se manda en "Aplicar ahora"; si no, lo vuelve vigente el cron en el corte.
        addonsNextCycle: pickAddons(editingData.addonsNextCycle),
        ...(applyAddonsNow && { addons: pickAddons(editingData.addonsNextCycle) }),
        // null limpia la fecha (vuelve a "con el corte"); el DTO ignora null.
        addonsNextCycleAt: editingData.addonsNextCycleAt || null,
        ...(editingData.subscriptionStartDate && { startsAt: editingData.subscriptionStartDate }),
        ...(editingData.subscriptionExpiresAt && { expiresAt: editingData.subscriptionExpiresAt })
      }
    };

    // Agrega business solo si hay nombre
    if (editingData.businessName?.trim()) {
      updatedData.businessName = editingData.businessName.trim();
    }

    // Limpia undefined
    Object.keys(updatedData).forEach(key => {
      if (updatedData[key] && typeof updatedData[key] === 'object') {
        Object.keys(updatedData[key]).forEach(subkey => {
          if (updatedData[key][subkey] === undefined) delete updatedData[key][subkey];
        });
      }
      if (!updatedData[key] || Object.keys(updatedData[key]).length === 0) {
        delete updatedData[key];
      }
    });

    return updatedData;
  }



  const sortHeaders = [
    { key: 'name', label: 'Cliente', align: 'left' },
    { key: 'expires', label: 'Suscripción', align: 'left' },
    { key: null, label: 'Conexiones', align: 'left' },
    { key: null, label: 'Última actividad', align: 'left' },
    { key: 'messages', label: 'Mensajes', align: 'right', hint: 'recibidos · enviados' },
    { key: 'appointments', label: 'Citas', align: 'right' },
    { key: null, label: 'Recordatorios', align: 'right' },
    { key: 'revenue', label: 'Valor agendado', align: 'right' },
    { key: null, label: '', align: 'right' },
  ];

  const periodControl = (
    <div className="sa-segment sa-segment-scroll" role="tablist" aria-label="Periodo">
      {PERIODS.map((p) => (
        <button
          key={p.key}
          type="button"
          role="tab"
          aria-selected={periodKey === p.key}
          className={`sa-segment-btn ${periodKey === p.key ? 'is-active' : ''}`}
          onClick={() => setPeriodKey(p.key)}
        >
          {p.label}
        </button>
      ))}
    </div>
  );

  return (
    <div className="sa-root">
      {/* Barra superior translúcida: el contenido pasa por debajo */}
      <header className="sa-topbar">
        <div className="sa-topbar-inner">
          <div className="sa-brand">
            <Shield size={18} className="sa-brand-icon" />
            <span className="sa-brand-name">Superadmin</span>
          </div>
          <nav className="sa-tabs" aria-label="Secciones">
            <button type="button" className={`sa-tab ${activeTab === 'clients' ? 'is-active' : ''}`} onClick={() => setActiveTab('clients')}>
              <Users size={15} /> Clientes
            </button>
            <button type="button" className={`sa-tab ${activeTab === 'platform' ? 'is-active' : ''}`} onClick={() => setActiveTab('platform')}>
              <Smartphone size={15} /> Plataforma
            </button>
          </nav>
          <div className="sa-topbar-actions">
            <PushToggle />
            <button
              type="button"
              className="sa-iconbtn"
              onClick={refreshAll}
              disabled={usageLoading}
              aria-label="Recargar la información"
              title="Recargar clientes, uso y estado de WhatsApp"
            >
              <RefreshCw size={16} className={usageLoading ? 'sa-spin' : ''} />
            </button>
            {onLogout && (
              <button type="button" className="sa-iconbtn" onClick={onLogout} aria-label="Cerrar sesión" title="Cerrar sesión">
                <LogOut size={16} />
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Indicador de "jalar para recargar" */}
      <div className={`sa-ptr ${ptr.dragging ? "is-dragging" : ""} ${ptr.refreshing ? "is-refreshing" : ""}`} style={{ height: ptr.pull }} aria-hidden>
        <RefreshCw size={20} style={{ transform: `rotate(${ptr.progress * 360}deg)`, opacity: 0.35 + ptr.progress * 0.65 }} />
      </div>

      <main className="sa-main">
        {activeTab === 'platform' && (
          <section className="sa-section">
            <PlatformSettings />
          </section>
        )}

        {activeTab === 'clients' && (
          <>
            <BusinessHeader portfolio={portfolio} usage={overview} periodLabel={period.label} leadsCount={leads.length} periodControl={periodControl} />

            <AttentionFeed
              items={attentionItems}
              onOpen={(client, tab) => openClient(client, tab)}
              onPay={(client) => setQuickPayClient(client)}
            />

            <ServerHealth health={serverHealth} />

            <section className="sa-section sa-clients">
              <div className="sa-toolbar">
                <h2 className="sa-h2">Clientes <span className="sa-count">{visibleClients.length}</span></h2>
                <label className="sa-search">
                  <Search size={15} />
                  <input
                    type="search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Buscar negocio, slug o usuario"
                    autoCapitalize="none"
                    autoCorrect="off"
                    autoComplete="off"
                    enterKeyHint="search"
                  />
                </label>
                {!isDesktop && (
                  <select className="sa-select" value={sortKey} onChange={(e) => setSortKey(e.target.value)} aria-label="Ordenar">
                    <option value="activity">Más activos</option>
                    <option value="messages">Más mensajes</option>
                    <option value="appointments">Más citas</option>
                    <option value="revenue">Más valor</option>
                    <option value="expires">Vencen antes</option>
                    <option value="name">Nombre</option>
                  </select>
                )}
                <button type="button" className="sa-btn sa-btn-primary" onClick={() => setShowNewClientForm(true)}>
                  <Plus size={16} /> <span className="sa-btn-label">Nuevo cliente</span>
                </button>
              </div>

              {visibleClients.length === 0 && (
                <div className="sa-empty">Sin clientes que coincidan con “{query}”.</div>
              )}

              {isDesktop ? (
                <div className="sa-table-wrap">
                  <table className="sa-table">
                    <thead>
                      <tr>
                        {sortHeaders.map((h, i) => (
                          <th
                            key={i}
                            className={h.key ? 'sortable' : ''}
                            style={{ textAlign: h.align }}
                            aria-sort={sortKey === h.key ? (sortDir === 'desc' ? 'descending' : 'ascending') : undefined}
                            onClick={h.key ? () => toggleSort(h.key) : undefined}
                            title={h.hint}
                          >
                            <span className={sortKey === h.key ? 'is-sorted' : ''}>
                              {h.label}
                              {h.key && (sortKey === h.key
                                ? (sortDir === 'desc' ? <ArrowDown size={12} /> : <ArrowUp size={12} />)
                                : <ArrowUpDown size={12} className="sa-sort-hint" />)}
                            </span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {visibleClients.map((client, i) => {
                        const { usage, st, conn, created, idle, lastEver, unhealthy, revenue, confirmPct } = rowData(client);
                        const selected = expandedClient === client.id;
                        return (
                          <tr
                            key={client.id}
                            className={`sa-row ${selected ? 'is-selected' : ''} ${idle && st.label !== 'Activa' ? 'is-idle' : ''}`}
                            style={{ '--i': i }}
                            onClick={() => toggleExpanded(client)}
                          >
                            <td>
                              <div className="sa-cell-title">{client.business.name}</div>
                              <div className="sa-cell-sub">@{client.auth.username} · {client.slug}</div>
                            </td>
                            <td>
                              <span className="sa-pill" style={{ '--pill': st.color }}>{st.label}</span>
                              <div className="sa-cell-sub">{st.relative || (st.expiresLabel ? `hasta ${st.expiresLabel}` : 'sin vigencia')}</div>
                              {idle && st.label === 'Activa' && <div className="sa-cell-warn">Sin uso en el periodo</div>}
                            </td>
                            <td>
                              <div className="sa-meta-item"><span className="sa-dot" style={{ background: conn.waConnected ? BRAND_COLORS.primary.main : BRAND_COLORS.error }} />WhatsApp</div>
                              <div className="sa-meta-item"><span className="sa-dot" style={{ background: conn.calendarsReady === 0 ? BRAND_COLORS.error : conn.calendarsReady >= conn.seats ? BRAND_COLORS.primary.main : BRAND_COLORS.warning }} />Calendar {conn.calendarsReady}/{conn.seats}</div>
                            </td>
                            <td className="sa-cell-muted">{relativeDay(lastEver)}</td>
                            <td className="num">{fmtInt(usage?.messagesReceived)} <span className="sa-cell-sub">· {fmtInt(usage?.messagesSent)}</span></td>
                            <td className="num">
                              {fmtInt(created)}
                              {confirmPct !== null && <div className="sa-cell-sub">{confirmPct}% conf.</div>}
                            </td>
                            <td className="num">
                              {fmtInt(usage?.remindersSent)}
                              {usage?.remindersFailed > 0 && <div className="sa-cell-warn">{fmtInt(usage.remindersFailed)} fallidos</div>}
                            </td>
                            <td className="num sa-cell-money">{fmtMoney(revenue)}</td>
                            <td className="num">
                              <span className="sa-row-end">
                                {unhealthy > 0 && <span className="sa-cell-warn" title="Errores del bot + caídas de WhatsApp + recordatorios fallidos">⚠ {fmtInt(unhealthy)}</span>}
                                <ChevronRight size={16} className="sa-chev" />
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <ul className="sa-list">
                  {visibleClients.map((client, i) => {
                    const { usage, st, conn, created, idle, lastEver, unhealthy, revenue } = rowData(client);
                    return (
                      <li key={client.id} style={{ '--i': i }}>
                        <button
                          type="button"
                          className={`sa-listrow ${idle && st.label !== 'Activa' ? 'is-idle' : ''}`}
                          onClick={() => toggleExpanded(client)}
                        >
                          <div className="sa-listrow-main">
                            <div className="sa-listrow-top">
                              <span className="sa-cell-title">{client.business.name}</span>
                              <span className="sa-pill" style={{ '--pill': st.color }}>{st.label}</span>
                            </div>
                            <div className="sa-listrow-sub">
                              <span className="sa-dot" style={{ background: conn.waConnected ? BRAND_COLORS.primary.main : BRAND_COLORS.error }} />
                              {st.relative || relativeDay(lastEver)}
                              {idle && st.label === 'Activa' && <span className="sa-cell-warn"> · sin uso</span>}
                              {unhealthy > 0 && <span className="sa-cell-warn"> · ⚠ {fmtInt(unhealthy)}</span>}
                            </div>
                            <div className="sa-listrow-stats">
                              <span><b>{fmtInt(usage?.messagesReceived)}</b> msgs</span>
                              <span><b>{fmtInt(created)}</b> citas</span>
                              <span className="sa-cell-money"><b>{fmtMoney(revenue)}</b></span>
                            </div>
                          </div>
                          <ChevronRight size={18} className="sa-chev" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          </>
        )}
      </main>

      {/* Detalle de cliente: panel lateral en PC, sheet en móvil */}
      <ClientDetailPanel
        client={selectedClient}
        open={!!selectedClient}
        onClose={closeClient}
        side={isDesktop ? 'right' : 'bottom'}
        initialTab={detailTab}
        waHref={waHrefFor(selectedClient)}
        period={period}
        usage={selectedClient ? usageByClient[selectedClient.id] : null}
        prevUsage={selectedClient ? prevUsageByClient[selectedClient.id] : null}
        row={selectedClient ? rowData(selectedClient) : null}
        editingData={editingData}
        setEditingData={setEditingData}
        paymentForm={paymentForm}
        setPaymentForm={setPaymentForm}
        payments={payments}
        computePrice={computePrice}
        getSubscriptionStatus={getSubscriptionStatus}
        onSave={() => handleUpdateClient(selectedClient?.id)}
        onApplyAddonsNow={() => handleUpdateClient(selectedClient?.id, { applyAddonsNow: true })}
        onRegisterPayment={() => handleRegisterPayment(selectedClient?.id)}
      />

      {/* Pago rápido desde la bandeja */}
      <QuickPaySheet
        client={quickPayClient}
        open={!!quickPayClient}
        side={isDesktop ? 'right' : 'bottom'}
        status={quickPayClient ? getSubscriptionStatus(quickPayClient.subscription?.expiresAt, quickPayClient.subscription?.isActive) : null}
        onClose={() => setQuickPayClient(null)}
        onConfirm={registerPayment}
      />

      {/* Alta de cliente */}
      <Sheet
        open={showNewClientForm}
        onClose={() => setShowNewClientForm(false)}
        side={isDesktop ? 'right' : 'bottom'}
        title="Nuevo cliente"
        subtitle="Alta manual (sin registro público)"
        footer={
          <button type="button" className="sa-btn sa-btn-primary sa-btn-block" onClick={handleAddClient}>
            <Save size={16} /> Crear cliente
          </button>
        }
      >
        <div className="sa-form">
          <label className="sa-field">
            <span>Nombre del negocio</span>
            <input value={newClient.name} onChange={(e) => setNewClient({ ...newClient, name: e.target.value })} placeholder="Ej: TurnLink Barbería" />
          </label>
          <label className="sa-field">
            <span>Nombre de usuario</span>
            <input value={newClient.username} onChange={(e) => setNewClient({ ...newClient, username: e.target.value })} placeholder="Ej: turnlink" autoCapitalize="none" autoCorrect="off" />
          </label>
          <label className="sa-field">
            <span>Prefijo URL</span>
            <input className="font-monospace" value={newClient.prefixUrl} onChange={(e) => setNewClient({ ...newClient, prefixUrl: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '') })} placeholder="Ej: sb, bella, cm" maxLength="25" autoCapitalize="none" autoCorrect="off" />
            <small>Solo letras y números → {displayBusinessUrl(newClient.prefixUrl || 'slug')}</small>
          </label>
          <label className="sa-field">
            <span>Contraseña inicial</span>
            <PasswordInput value={newClient.password} onChange={(e) => setNewClient({ ...newClient, password: e.target.value })} placeholder="••••••••" autoComplete="new-password" />
          </label>
        </div>
      </Sheet>
    </div>
  )
}

export default SuperadminPanel;
